import React, { useState, useEffect } from 'react';
import { Users, UserPlus, Search, AlertTriangle } from 'lucide-react';
import AdminPasswordChange from './AdminPasswordChange';
import UserCard from './UserCard';
import UserForm from './UserForm';
import { useAuth } from '../../contexts/AuthContext';


const API_BASE_URL = 'https://ior.org/risk-register/web/api';

// Error message component
const ErrorAlert = ({ message, onClose }) => (
  <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-400 text-red-700 relative">
    <div className="flex items-center">
      <AlertTriangle className="w-5 h-5 mr-2" />
      <span>{message}</span>
    </div>
    {onClose && (
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-red-400 hover:text-red-600"
      >
        ×
      </button>
    )}
  </div>
);

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [passwordChangeUser, setPasswordChangeUser] = useState(null);
  const { user } = useAuth();

  // Fetch users
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem('authToken');

        const response = await fetch(
          `${API_BASE_URL}/users.php?role=${filterRole}&search=${searchTerm}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        const data = await response.json();

        if (data.success) {
          setUsers(data.data);
          setStats(data.stats);
        } else {
          throw new Error(data.error || 'Failed to fetch users');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(() => {
      fetchUsers();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, filterRole]);

  // Handle password change
  const handlePasswordChange = async (userId, newPassword) => {
    try {
      setError(null);
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_BASE_URL}/auth.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'admin-change-password',
          userId: userId,
          newPassword: newPassword
        }),
      });

      const data = await response.json();

      if (data.success) {
        setPasswordChangeUser(null);
        // Optional: Add success message state and display
      } else {
        throw new Error(data.error || 'Failed to change password');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Handle user deletion
  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    if(userId === user.id){
      window.confirm('Sorry, you can not delete yourself.')
      return;
    }

    try {
      setError(null);
      const token = localStorage.getItem('authToken');

      const response = await fetch(`${API_BASE_URL}/users.php?id=${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setUsers(current => current.filter(user => user.id !== userId));
      } else {
        throw new Error(data.error || 'Failed to delete user');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Handle user update
  const handleUpdateUser = async (userData) => {
    try {
      setError(null);
      const token = localStorage.getItem('authToken');

      console.log(userData);

      const response = await fetch(`${API_BASE_URL}/users.php?id=${userData.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (data.success) {
        setUsers(current =>
          current.map(user =>
            user.id === userData.id ? { ...user, ...userData } : user
          )
        );
        setEditingUser(null);
      } else {
        throw new Error(data.error || 'Failed to update user');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Handle adding new user
  const handleAddUser = async (userData) => {
    try {
      setError(null);
      const token = localStorage.getItem('authToken');

      const response = await fetch(`${API_BASE_URL}/users.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (data.success) {
        setUsers(current => [...current, data.data]);
        setIsAddingUser(false);
      } else {
        throw new Error(data.error || 'Failed to add user');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2">User Management</h1>
        <div className="text-gray-600">
          Manage users, roles, and permissions
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <ErrorAlert 
          message={error} 
          onClose={() => setError(null)} 
        />
      )}

      {/* Controls */}
      <div className="mb-6 flex justify-between items-start">
        <div className="flex-1 grid grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search users..."
              className="pl-10 pr-4 py-2 w-full border rounded-md"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            className="px-4 py-2 border rounded-md"
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
          >
            <option value="all">All Roles</option>
            <option value="admin">Administrators</option>
            <option value="smt_member">SMT Members</option>
            <option value="gb_member">GB Members</option>
            <option value="staff">Staff</option>
          </select>
        </div>
        <button
          onClick={() => setIsAddingUser(true)}
          className="ml-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Add User
        </button>
      </div>

      {/* Users Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {loading ? (
          <div className="col-span-2 flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : users.length === 0 ? (
          <div className="col-span-2 text-center py-8 text-gray-500">
            No users found
          </div>
        ) : (
          users.map(user => (
            <UserCard
              key={user.id}
              user={user}
              onEdit={setEditingUser}
              onDelete={handleDeleteUser}
              onChangePassword={setPasswordChangeUser}
            />
          ))
        )}
      </div>

      {/* Modals */}
      {isAddingUser && (
        <UserForm
          onSubmit={handleAddUser}
          onCancel={() => setIsAddingUser(false)}
        />
      )}

      {editingUser && (
        <UserForm
          user={editingUser}
          onSubmit={handleUpdateUser}
          onCancel={() => setEditingUser(null)}
        />
      )}

      {passwordChangeUser && (
        <AdminPasswordChange
          user={passwordChangeUser}
          onSubmit={handlePasswordChange}
          onClose={() => setPasswordChangeUser(null)}
        />
      )}
    </div>
  );
};

export default UserManagement;
